import React, {useCallback} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DuplicateStepOptionWithStepLimit} from 'procedures/design/step-config/StepLimitHelpers';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

export interface UseStepOverflowMenuParams {
  moveStep: (stepGroupIndex: number, stepIndex: number, direction: number) => void;
  copyStep: (stepGroupIndex: number, stepIndex: number) => Promise<void>;
  deleteStepWithConfirmation: (stepCid: string) => Promise<void>;
  isStepLimitReached: boolean;
  procedureType: ProcedureType;
}

/**
 * Builds the overflow menu options for each step in the StepSelectorSidebar.
 * Composes step operations (move, copy, delete) into menu items with appropriate
 * labels, icons, and disabled states.
 */
export default function useStepOverflowMenu(params: UseStepOverflowMenuParams): {
  makeOverflowMenu: (
    stepGroupIndex: number,
    stepIndex: number,
    numberOfStepsInGroup: number,
    stepCid: string
  ) => Array<OverflowMenuOption>;
} {
  const {moveStep, copyStep, deleteStepWithConfirmation, isStepLimitReached, procedureType} = params;

  const makeOverflowMenu = useCallback(
    (
      stepGroupIndex: number,
      stepIndex: number,
      numberOfStepsInGroup: number,
      stepCid: string
    ): Array<OverflowMenuOption> => [
      {
        icon: 'copy',
        contents: (
          <DuplicateStepOptionWithStepLimit
            isStepLimitReached={isStepLimitReached}
            procedureType={procedureType}
          />
        ),
        onSelect: async () => {
          await copyStep(stepGroupIndex, stepIndex);
        },
        isDisabled: isStepLimitReached,
      },
      {
        icon: 'arrow-up',
        contents: <>Move up</>,
        onSelect: () => {
          moveStep(stepGroupIndex, stepIndex, -1);
        },
        isDisabled: stepIndex === 0,
      },
      {
        icon: 'arrow-down',
        contents: <>Move down</>,
        onSelect: () => {
          moveStep(stepGroupIndex, stepIndex, +1);
        },
        isDisabled: stepIndex === numberOfStepsInGroup - 1,
      },
      {
        icon: 'trash-can',
        contents: <>Remove</>,
        onSelect: async () => {
          await deleteStepWithConfirmation(stepCid);
        },
        isDisabled: false,
      },
    ],
    [copyStep, deleteStepWithConfirmation, isStepLimitReached, moveStep, procedureType]
  );

  return {makeOverflowMenu};
}
